import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Img from 'react-bootstrap/Image';
import { changePassword, validateResetToken } from '../services/authService';
import { showError, showSuccess } from '../utils/toast';

function ResetPassword() {
  const { id, token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [validLink, setValidLink] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let active = true;

    const validateLink = async () => {
      setValidating(true);
      try {
        const response = await validateResetToken(id, token);
        if (!active) return;
        setValidLink(true);
        setMaskedEmail(response.email || '');
      } catch (error) {
        if (!active) return;
        setValidLink(false);
        showError(error.message || 'Invalid or expired reset link.');
      } finally {
        if (active) setValidating(false);
      }
    };

    validateLink();
    return () => {
      active = false;
    };
  }, [id, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < 6) {
      showError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      showError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await changePassword(id, token, password);
      setCompleted(true);
      showSuccess('Password updated successfully. You can sign in now.');
    } catch (error) {
      showError(error.message || 'Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hr-login-page">
      <Form onSubmit={handleSubmit} className="hr-login-card">
        <div className="text-center mb-4">
          <Img src="./Images/LOGO_ORNNOVA.avif" style={{ width: '200px' }} alt="Ornnova logo" />
          <h4 className="mt-3 mb-1">Reset Password</h4>
          <p className="text-muted mb-0">Choose a new password for your account</p>
        </div>
        <hr />

        {validating && <p className="text-center hr-page-hint">Validating reset link...</p>}

        {!validating && !validLink && (
          <div className="text-center">
            <p className="text-danger mb-3">This reset link is invalid or has expired.</p>
            <Link to="/forgotpassword" className="hr-login-link">Request a new link</Link>
          </div>
        )}

        {!validating && validLink && completed && (
          <div className="text-center">
            <p className="mb-3">Your password has been updated.</p>
            <Link to="/" className="hr-login-link">Go to Sign In</Link>
          </div>
        )}

        {!validating && validLink && !completed && (
          <>
            {maskedEmail && (
              <p className="text-center text-muted mb-3">Account: {maskedEmail}</p>
            )}
            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label><b>New Password</b></Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={6}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="confirmPassword">
              <Form.Label><b>Confirm Password</b></Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength={6}
                required
              />
            </Form.Group>
            <div className="text-center d-grid gap-2">
              <Button className="hr-btn-accent px-4" type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
              <Link to="/" className="hr-login-link">Back to Sign In</Link>
            </div>
          </>
        )}
      </Form>
    </div>
  );
}

export default ResetPassword;
